import { ReactElement } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

// project imports
import { GRID_SPACING } from 'utils/constants';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { openSnackbar, setAppErrorId } from 'features/app/appSlice';
import { selectAppErrorId } from 'features/app/appSelectors';
import Snackbar from 'components/assets/Snackbar';
import AnimateButton from 'components/assets/AnimateButton';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import RestoreIcon from '@mui/icons-material/Restore';
import ContentCopy from '@mui/icons-material/ContentCopy';
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography
} from '@mui/material';

// assets
import imageGrid from 'assets/images/maintenance/img-soon-grid.svg';
import imageDarkGrid from 'assets/images/maintenance/img-soon-grid-dark.svg';
import imageBlock from 'assets/images/maintenance/img-soon-block.svg';
import imageBlueBlock from 'assets/images/maintenance/img-soon-blue-block.svg';
import imagePurpleBlock from 'assets/images/maintenance/img-soon-purple-block.svg';

// styles
const CardMediaWrapper = styled('div')({
  maxWidth: 720,
  margin: '0 auto',
  position: 'relative'
});

const PageContentWrapper = styled('div')({
  maxWidth: 450,
  margin: '0 auto',
  textAlign: 'center'
});

const ComingSoonCard = styled(Card)({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const CardMediaBlock = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  animation: '8s blink ease-in-out infinite'
});

const CardMediaBlue = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  animation: '15s wings ease-in-out infinite'
});

const CardMediaPurple = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  animation: '12s wings ease-in-out infinite'
});

// ==============================|| CRASH FALLBACK ||============================== //

const CrashFallback = ({
  resetErrorBoundary,
  error
}: FallbackProps): ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const appErrorId: string | undefined = useAppSelector(selectAppErrorId);

  const copyErrorToClipboard = () => {
    navigator.clipboard
      .writeText(
        `ID: ${appErrorId}, ERROR: ${error.name}, MESSAGE: ${error.message}`
      )
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: `${t(
              'pages.crashFallback.messageCopied'
            )} "ID: ${appErrorId}, ERROR: ${error.name}, MESSAGE: ${
              error.message
            }"`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
      });
  };

  const resetBoundaries = () => {
    dispatch(setAppErrorId(undefined));
    resetErrorBoundary();
  };

  return (
    <>
      <ComingSoonCard>
        <CardContent>
          <Grid container justifyContent='center' spacing={GRID_SPACING}>
            <Grid item xs={12}>
              <PageContentWrapper>
                <Grid container spacing={GRID_SPACING}>
                  <Grid item xs={12}>
                    <Typography variant='h2'>
                      {t('pages.crashFallback.headline')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1'>
                      {t('pages.crashFallback.message')}
                    </Typography>
                  </Grid>
                </Grid>
              </PageContentWrapper>
            </Grid>
            <Grid item xs={12}>
              <CardMediaWrapper>
                <CardMedia
                  component='img'
                  image={
                    theme.palette.mode === 'dark' ? imageDarkGrid : imageGrid
                  }
                />
                <CardMediaBlock src={imageBlock} />
                <CardMediaBlue src={imageBlueBlock} />
                <CardMediaPurple src={imagePurpleBlock} />
              </CardMediaWrapper>
            </Grid>
            <Grid item xs={12}></Grid>
            <Grid item xs={12}>
              <PageContentWrapper>
                <Grid container spacing={GRID_SPACING} justifyContent='center'>
                  <Grid item>
                    <AnimateButton>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => copyErrorToClipboard()}
                      >
                        <ContentCopy sx={{ fontSize: '1.3rem', mr: 0.75 }} />
                        {t('pages.crashFallback.button.copy')}
                      </Button>
                    </AnimateButton>
                  </Grid>

                  <Grid item>
                    <AnimateButton>
                      <Button
                        variant='contained'
                        size='large'
                        onClick={() => resetBoundaries()}
                      >
                        <RestoreIcon sx={{ fontSize: '1.3rem', mr: 0.75 }} />
                        {t('pages.crashFallback.button.restore')}
                      </Button>
                    </AnimateButton>
                  </Grid>
                </Grid>
              </PageContentWrapper>
            </Grid>
          </Grid>
        </CardContent>
      </ComingSoonCard>
      <Snackbar />
    </>
  );
};

export default CrashFallback;
